import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';

import { RoleCode } from '@ninety/platform/models/_shared/role-code';
import { FeatureFlagKeys, selectFeatureFlag, UsersStateActions, selectCurrentUser } from '@ninety/platform/state';
import {
  selectCurrentSubscriptionConfig,
  selectIsPaidPlan,
} from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { TerraModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

const TARGET_ROLES = [RoleCode.manager, RoleCode.admin, RoleCode.owner];

@Component({
  selector: 'ninety-manager-permission-changes-banner',
  templateUrl: './manager-permission-changes-banner.component.html',
  styleUrls: ['./manager-permission-changes-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, TerraModule, ButtonComponent, PushPipe, LetDirective],
})
export class ManagerPermissionChangesBannerComponent {
  private readonly store = inject(Store);

  protected readonly currentUser$ = this.store.select(selectCurrentUser);
  protected readonly isPaid$ = this.store.select(selectIsPaidPlan);
  protected readonly subscriptionConfiguration$ = this.store.select(selectCurrentSubscriptionConfig);
  protected readonly managerPermissionChangesEnabled$ = this.store.select(
    selectFeatureFlag(FeatureFlagKeys.inAppManagerPermissionChangesBanner)
  );

  protected readonly shouldShowBanner$ = combineLatest([
    this.currentUser$,
    this.isPaid$,
    this.managerPermissionChangesEnabled$,
    this.subscriptionConfiguration$,
  ]).pipe(
    map(([currentUser, isPaid, flagEnabled, subConfig]) => {
      const isEligibleRole = TARGET_ROLES.includes(currentUser?.roleCode);
      const dismissed = currentUser?.managerPermissionChangesNotificationDismissed ?? false;

      return !dismissed && isEligibleRole && isPaid && flagEnabled && !!subConfig?.id;
    })
  );

  protected getCopy(roleCode: RoleCode) {
    switch (roleCode) {
      // NOTE: owners/admins get the same copy
      case RoleCode.owner:
      case RoleCode.admin:
        return 'Heads up: billing and license management is now limited to Owner and Admin roles only. Managers in your company will now request these changes from you.';
      default:
        return 'Billing and license management is now limited to Owner and Admin roles. For subscription changes or seat adjustments, please contact your Owner or Admin.';
    }
  }

  dismissManagerPermissionChangesNotification() {
    this.store.dispatch(UsersStateActions.dismissManagerPermissionChangesNotification());
  }
}
